import { Component } from '@theme/component';
import { debounce } from '@theme/utilities';

/**
 * `<video-testimonials>` — a scroll-snap carousel of review cards with dot
 * pagination.
 *
 * Progressive enhancement: the track is a native horizontal scroller, so swiping
 * works with JS off. JS adds dot navigation, active-dot tracking, and reveals each
 * card's "read more" trigger only when its quote is actually truncated.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} track
 *
 * @extends {Component<Refs>}
 */
class VideoTestimonials extends Component {
  requiredRefs = ['track'];

  /** @type {IntersectionObserver | undefined} */
  #observer;

  connectedCallback() {
    super.connectedCallback();

    this.#observer = new IntersectionObserver(this.#onIntersect, {
      root: this.refs.track,
      threshold: 0.6,
    });
    for (const slide of this.#slides) this.#observer.observe(slide);

    requestAnimationFrame(this.#refreshMoreButtons);
    document.fonts?.ready.then(this.#refreshMoreButtons);
    window.addEventListener('resize', this.#onResize);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#observer?.disconnect();
    window.removeEventListener('resize', this.#onResize);
  }

  /** Scroll to the slide named by the clicked dot's `data-index`. */
  goToSlide = (event) => {
    const dot = event.target;
    if (!(dot instanceof HTMLElement)) return;

    const index = Number(dot.dataset.index);
    const slide = this.#slides[index];
    if (!slide) return;

    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.refs.track.scrollTo({
      left: slide.offsetLeft - this.refs.track.offsetLeft,
      behavior: reduceMotion ? 'auto' : 'smooth',
    });
  };

  /** @returns {HTMLElement[]} */
  get #slides() {
    return /** @type {HTMLElement[]} */ (Array.from(this.querySelectorAll('.video-testimonials__slide')));
  }

  /** @param {IntersectionObserverEntry[]} entries */
  #onIntersect = (entries) => {
    for (const entry of entries) {
      if (!entry.isIntersecting) continue;
      const index = this.#slides.indexOf(/** @type {HTMLElement} */ (entry.target));
      this.#setActiveDot(index);
    }
  };

  /** @param {number} index */
  #setActiveDot(index) {
    const dots = this.querySelectorAll('.video-testimonials__dot');
    dots.forEach((dot, i) => {
      if (i === index) dot.setAttribute('aria-current', 'true');
      else dot.removeAttribute('aria-current');
    });
  }

  #onResize = debounce(() => this.#refreshMoreButtons(), 150);

  /** Reveal the "read more" trigger only on cards whose quote is truncated. */
  #refreshMoreButtons = () => {
    for (const quote of this.querySelectorAll('.video-testimonials__quote')) {
      if (!(quote instanceof HTMLElement)) continue;
      const card = quote.closest('.video-testimonials__card');
      const button = card?.querySelector('.video-testimonials__more');
      if (!button) continue;
      const truncated = quote.scrollHeight - quote.clientHeight > 1;
      button.toggleAttribute('hidden', !truncated);
    }
  };
}

if (!customElements.get('video-testimonials')) {
  customElements.define('video-testimonials', VideoTestimonials);
}

/**
 * `<video-testimonial-player>` — lazily plays its `<video>` when the surrounding
 * `<dialog-component>` opens and stops/rewinds it on close, so no media loads
 * until a card's video is actually opened.
 *
 * @typedef {object} PlayerRefs
 * @property {HTMLVideoElement} video
 *
 * @extends {Component<PlayerRefs>}
 */
class VideoTestimonialPlayer extends Component {
  requiredRefs = ['video'];

  /** @type {Element | null} */
  #dialog = null;

  connectedCallback() {
    super.connectedCallback();
    this.#dialog = this.closest('dialog-component');
    if (!this.#dialog) return;
    this.#dialog.addEventListener('dialog:open', this.#play);
    this.#dialog.addEventListener('dialog:close', this.#stop);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (!this.#dialog) return;
    this.#dialog.removeEventListener('dialog:open', this.#play);
    this.#dialog.removeEventListener('dialog:close', this.#stop);
  }

  #play = () => {
    this.refs.video.play?.().catch(() => {});
  };

  #stop = () => {
    const { video } = this.refs;
    video.pause();
    video.currentTime = 0;
  };
}

if (!customElements.get('video-testimonial-player')) {
  customElements.define('video-testimonial-player', VideoTestimonialPlayer);
}
